// Captura de firma en un canvas (mouse o dedo/táctil). Devuelve la firma como
// data URL (PNG) vía onChange. Reutilizable: firma del paciente en recetas,
// certificados y consentimientos.
import { useEffect, useRef, useState } from 'react';
import { Eraser, PenLine } from 'lucide-react';

export function FirmaCanvas({ value, onChange, label = 'Firma', alto = 140 }: {
  value: string; onChange: (v: string) => void; label?: string; alto?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dibujando = useRef(false);
  const movido = useRef(false);
  const [vacio, setVacio] = useState(!value);

  // Prepara el lienzo (resolución por DPR para que no se vea borroso) y, si ya
  // hay una firma previa (edición), la pinta.
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    c.width = w * dpr; c.height = alto * dpr;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#111';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, alto);
      img.src = value;
    }
  }, [alto, value]);

  const pos = (e: React.PointerEvent) => {
    const c = ref.current!;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    const ctx = ref.current?.getContext('2d'); if (!ctx) return;
    dibujando.current = true; movido.current = false;
    const { x, y } = pos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
    ref.current?.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent) => {
    if (!dibujando.current) return;
    const ctx = ref.current?.getContext('2d'); if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y); ctx.stroke(); movido.current = true;
  };
  const end = () => {
    if (!dibujando.current) return;
    dibujando.current = false;
    const c = ref.current; if (!c) return;
    if (!movido.current) return;  // un tap sin trazo no cuenta como firma
    setVacio(false);
    onChange(c.toDataURL('image/png'));
  };
  const limpiar = () => {
    const c = ref.current; const ctx = c?.getContext('2d');
    if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
    setVacio(true); onChange('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> {label}</span>
        <button type="button" onClick={limpiar} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"><Eraser className="w-3.5 h-3.5" /> Limpiar</button>
      </div>
      <div className="relative rounded-md border bg-white">
        <canvas
          ref={ref}
          style={{ height: alto, touchAction: 'none' }}
          className="w-full rounded-md cursor-crosshair"
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
        />
        {vacio && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">Firme aquí con el mouse o el dedo</span>}
      </div>
    </div>
  );
}
